import {ChangeDetectionStrategy, Component, computed, inject, Signal} from "@angular/core";
import {IdentificationMode} from '../identity/identification-page/identification-mode';
import {IdentityModel, LoginStatus} from '../identity/identity.model';
import {SessionModel} from "../session/session.model";
import {AnalyticsService} from '../logging/analytics.service';
import {Page} from "../navigation/page";
import {ButtonComponent} from "../buttons/button/button.component";
import {ButtonConnotation, ButtonProminence} from "../buttons/button/button";
import {IconButtonComponent} from "../buttons/icon-button/icon-button.component";
import {IconButtonType} from "../buttons/icon-button/icon-button";
import {OrderingContextModel} from '../ordering-context/ordering-context.model';
import {formatDateTimeForDisplay} from '../utils/date-time-utils';
import {CustomerDetails, CustomerDetailsService} from '../customer-info/customer-details.service';
import {AppDeviceService} from '../app-device/app-device.service';
import {NotificationService} from "../notification/notification.service";
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ModalPageLayoutComponent} from '../page-layout/modal-page-layout/modal-page-layout.component';
import {ChewzieAttributionComponent} from "../ui-elements/chewzie-attribution/chewzie-attribution.component";
import {LegalPanelComponent} from '../legal/legal-panel/legal-panel.component';
import {environment} from '../../environments/environment';
import {ConfigurationModel} from '../configuration/configuration.model';
import {OrderModel} from '../order/order.model';
import {ActivatableDirective} from '../ui-elements/activatable.directive';

@Component({
    selector: "woa-nav-menu-page",
    templateUrl: "./nav-menu-page.component.html",
    styleUrl: "./nav-menu-page.component.scss",
    providers: [{provide: Page, useExisting: NavMenuPageComponent}],
    imports: [
        ButtonComponent,
        ChewzieAttributionComponent,
        IconButtonComponent,
        LegalPanelComponent,
        ModalPageLayoutComponent,
        NgClass,
        TranslateModule,
        ActivatableDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavMenuPageComponent extends Page {
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    public sessionModel: SessionModel = inject(SessionModel);
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public identityModel: IdentityModel = inject(IdentityModel);
    public appDeviceService: AppDeviceService = inject(AppDeviceService);
    private analyticsService: AnalyticsService = inject(AnalyticsService);
    private notificationService: NotificationService = inject(NotificationService);

    ButtonProminence = ButtonProminence;
    ButtonConnotation = ButtonConnotation;
    IconButtonType = IconButtonType;
    formatDateTimeForDisplay = formatDateTimeForDisplay;

    customerDetails: CustomerDetails;

    public readonly version: Signal<string> = computed<string>(() =>
        !!this.appDeviceService.platformVersion() ? environment.version + " : " + this.appDeviceService.platformVersion() : environment.version);

    public readonly profileName: Signal<string> = computed<string>(() =>
        !this.identityModel.isGuestUser() && this.identityModel.currentIdentity().firstName ? this.identityModel.currentIdentity().firstName :
            this.customerDetails ? this.customerDetails.firstName
                : undefined);

    public readonly showWaiterName: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.requireWaiterName() && !!this.identityModel.waiterName());

    public readonly currentStoreLabel: Signal<string> = computed<string>(() =>
        this.orderingContextModel.canChangeStore() ? "woa.label.nav-menu.current-store" : "woa.label.nav-menu.single-store");

    public readonly showFeedback: Signal<boolean> = computed<boolean>(() =>
        this.configurationModel.app()?.feedbackCategories.length > 0 && this.configurationModel.isConsumerActor());

    constructor() {
        const customerDetailsService: CustomerDetailsService = inject(CustomerDetailsService);
        super();
        this.customerDetails = customerDetailsService.loadCustomer();
    }

    async login(): Promise<void> {
        void this.analyticsService.trackNavOption("login");
        void this.dismiss();
        return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.LOGIN});
    }

    async register(): Promise<void> {
        void this.analyticsService.trackNavOption("register");
        void this.dismiss();
        return this.navigationService.createPage("woa-identification-page", {"mode": IdentificationMode.REGISTER});
    }

    async logout(): Promise<void> {
        void this.analyticsService.trackNavOption("logout");
        void this.dismiss();
        if (this.configurationModel.requireWaiterName() || (await this.identityModel.loginAsGuest()).status !== LoginStatus.SUCCESS) {
            void this.sessionModel.goToEntryPage();
        }
    }

    async changeWaiterName(): Promise<void> {
        void this.dismiss();
        const name: string = await this.navigationService.createPage("woa-waiter-name-edit-page", {
            name: this.identityModel.waiterName()
        });
        if (name && name.trim()) {
            this.identityModel.waiterName.set(name.trim());
        }
    }

    async profile(): Promise<void> {
        void this.analyticsService.trackNavOption("profile");
        void this.dismiss();
        return this.navigationService.createPage("woa-profile-edit-page");
    }

    async changePassword(): Promise<void> {
        void this.analyticsService.trackNavOption("password");
        void this.dismiss();
        return this.navigationService.createPage("woa-change-password-page");
    }

    async loyaltyCards(): Promise<void> {
        void this.analyticsService.trackNavOption("loyalty-cards");
        void this.dismiss();
        return this.navigationService.createPage("woa-loyalty-cards-page");
    }

    async orders(): Promise<void> {
        void this.analyticsService.trackNavOption("orders");
        void this.dismiss();
        return this.navigationService.createPage("woa-customer-orders-page");
    }

    async deals(): Promise<void> {
        void this.analyticsService.trackNavOption("deals");
        void this.dismiss();
        return this.navigationService.createPage("woa-deals-page");
    }

    async stores(): Promise<void> {
        void this.analyticsService.trackNavOption("stores");
        void this.dismiss();
        void this.orderingContextModel.viewStores();
    }

    async currentStore(): Promise<void> {
        void this.analyticsService.trackNavOption("current-store");
        void this.dismiss();
        return this.orderingContextModel.viewCurrentStore();
    }

    async feedback(): Promise<void> {
        void this.analyticsService.trackNavOption("feedback");
        void this.dismiss();
        return this.navigationService.createPage("woa-feedback-page");
    }

    public async exit(): Promise<void> {
        if (await this.notificationService.displayConfirmation("woa.heading.exit-app", undefined, this.orderModel.orderHasItems() ? "woa.message.exit-app" : undefined,
            "woa.button.common.noThanks", "woa.button.exit-app", true)) {
            parent.postMessage("exit-app", "*");
        }
    }

    async exitToAppDevice(): Promise<void> {
        void this.dismiss();
        if (await this.notificationService.displayConfirmation("woa.heading.app-device.confirmExit", undefined,
            "woa.message.app-device.confirmExit", "woa.button.common.cancel", "woa.button.common.proceed", true)) {
            void this.appDeviceService.exitToAppDevice();
        }
    }

    async appDeviceReload(): Promise<void> {
        void this.dismiss();
        void this.appDeviceService.reloadAppDevice();
    }

    async appDeviceStatus(): Promise<void> {
        void this.analyticsService.trackNavOption("app-device-status");
        void this.dismiss();
        return this.navigationService.createPage("woa-app-device-status-page");
    }
}
